import React, { useEffect, useState } from 'react';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import Papa from 'papaparse';

import ActionButtons from './ActionButtons';
import UploadComponent from './UploadComponent';
import useProjectTitleValidation from '../../../hooks/useProjectTitleValidation';
// import { processImages } from '../../../utils/helpers'; // Disabled - using Firebase extension
import Header from '../../Header';
import { db } from '../../../firebase';

import './styles.css';
import { uploadFile } from '../../../utils/storageHelpers';
import LinearProgressWithLabel from '../LinerProgressWithLabel';

const AddProject = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [csvFile, setCSVFile] = useState();
	const [images, setImages] = useState([]);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [csvUploadProgress, setCsvUploadProgress] = useState(0);
	const [imageUploadCount, setImageUploadCount] = useState(0);
	const [imageUploadFailed, setImageUploadFailed] = useState(0);
	const [totalImages, setTotalImages] = useState(0);
	const [projectName, projectNameInvalid, validateProjectTitle] = useProjectTitleValidation('');
	const [addedProjectId, setAddedProjectId] = useState('');
	const [isCSVSanitized, setIsCSVSanitized] = useState(false);
	const steps = [
		{ label: 'Section details',    sub: 'Name this section' },
		{ label: 'Upload CSV',          sub: 'Route and metadata file'      },
		{ label: 'Upload images',       sub: 'Captured route imagery'       },
		{ label: 'Processing & upload', sub: 'Saving and uploading files'   },
	];

	const { organizationId, directoryId } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (csvUploadProgress > 0 || imageUploadCount > 0) {
				e.preventDefault();
				e.returnValue = 'Section is uploading, are you sure you want to leave?';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [csvUploadProgress, imageUploadCount]);

	const handleNext = () => {
		if (activeStep !== 2) {
			if (activeStep === 0 && !projectName) {
				validateProjectTitle('');
				return;
			}
			if (activeStep === 1 && !csvFile) {
				return;
			}
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		} else if (activeStep === 2 && images.length !== 0) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
			addProject();
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const onProjectTitleChange = (e) => {
		validateProjectTitle(e.target.value);
	};

	const uploadFiles = async (projectId) => {
		await Promise.all([
			uploadImages(projectId),
			uploadCsvFile(projectId).then((csvUrl) => {
				const projectRef = doc(db, 'projects', projectId);
				return updateDoc(projectRef, { csvURL: csvUrl });
			}),
		]);
		
		setUploadComplete(true);
	};

	const uploadCsvFile = async (projectId) => {
		if (!csvFile) return;

		const path = '';
		try {
			return uploadFile(
				projectId,
				projectName,
				csvFile,
				path,
				(progress) => setCsvUploadProgress(progress),
				'reference.csv'
			);
		} catch (e) {
			console.error('Error uploading CSV file: ', e);
		}
	};

	const uploadImages = async (projectId) => {
		if (!images || images.length === 0) return;

		setTotalImages(images.length);
		const sortedImages = images.sort((a, b) => {
			const nameA = a.name.toUpperCase();
			const nameB = b.name.toUpperCase();
			return nameA.localeCompare(nameB);
		});

		const maxConcurrentUploads = 10;
		let activeUploads = 0;
		let currentIndex = 0;

		const uploadWithRetry = async (file, retries = 3) => {
			for (let attempt = 1; attempt <= retries; attempt++) {
				try {
					await uploadFile(
						projectId,
						projectName,
						file,
						'',  // Upload to project root, extension will process to /images
						() => {},
						undefined,
						{ directoryId, projectId },
						true  // skipDownloadURL - extension deletes original after converting
					);
					return true;
				} catch (error) {
					if (attempt === retries) {
						console.error(`Error uploading file (gave up after ${retries} attempts): ${file.name}`, error);
						return false;
					}
					// Exponential backoff before retry
					await new Promise(r => setTimeout(r, 1000 * attempt));
				}
			}
		};

		const enqueueUpload = async () => {
			if (currentIndex >= sortedImages.length) {
				return;
			}

			const file = sortedImages[currentIndex++];
			activeUploads++;

			const success = await uploadWithRetry(file);
			if (success) {
				setImageUploadCount((prevCount) => prevCount + 1);
			} else {
				setImageUploadFailed((prevCount) => prevCount + 1);
			}

			activeUploads--;
			enqueueUpload();
		};

		for (let i = 0; i < Math.min(maxConcurrentUploads, sortedImages.length); i++) {
			enqueueUpload();
		}

		await new Promise((resolve) => {
			const checkCompletion = setInterval(() => {
				if (currentIndex === sortedImages.length && activeUploads === 0) {
					clearInterval(checkCompletion);
					resolve();
				}
			}, 100);
		});
	};

	const sanitizeCSV = (projectId) => {
		const imageNames = images.map((image) => image.name.replace(/\.(jpg|webp)$/i, '').replace(/_\d+x\d+$/, ''));
		console.log('[sanitizeCSV] projectId:', projectId);
		console.log('[sanitizeCSV] csvFile:', csvFile);
		console.log('[sanitizeCSV] imageNames (count):', imageNames.length, imageNames.slice(0, 5));
		const sanitizedRows = [];
		const validImageNames = [];

		Papa.parse(csvFile, {
			delimiter: '\t',
			quoteChar: '"',
			header: true,
			transformHeader: (h) => {
				const cleaned = h.trim().replace(/^\uFEFF/, '');
				console.log('[sanitizeCSV] header:', JSON.stringify(h), '→', JSON.stringify(cleaned));
				return cleaned;
			},
			step: (row) => {
				try {
					const rawFileName = row.data.file_name;
					const fileNameFromCSV = (rawFileName || '').trim().replace(/\.(jpg|webp)$/i, '');
					const matched = fileNameFromCSV && imageNames.includes(fileNameFromCSV);
					if (!matched) {
						console.warn(
							`[sanitizeCSV] Row skipped — raw: ${JSON.stringify(rawFileName)}, normalised: ${JSON.stringify(fileNameFromCSV)}, ` +
							`sample imageNames: ${JSON.stringify(imageNames.slice(0, 3))}`
						);
					}
					if (matched) {
						sanitizedRows.push(row.data);
						validImageNames.push(fileNameFromCSV);
					}
				} catch (err) {
					console.error('[sanitizeCSV] Error processing row:', err.message || err, row);
				}
			},
			complete: async () => {
				console.log('[sanitizeCSV] complete — sanitizedRows:', sanitizedRows.length, ', validImageNames:', validImageNames.length);
				const csvString = Papa.unparse(sanitizedRows, {
					delimiter: '\t',
				});
				console.log('[sanitizeCSV] unparse output length:', csvString.length);
				const sanitizedBlob = new Blob([csvString], { type: 'text/csv' });
				const sanitizedFile = new File([sanitizedBlob], 'reference.csv', { type: 'text/csv' });
				const imagesToUpload = images.filter((img) =>
					validImageNames.includes(img.name.replace(/\.(jpg|webp)$/i, '').replace(/_\d+x\d+$/, ''))
				);
				setImages(imagesToUpload);
				setCSVFile(sanitizedFile);

				// Write ordered image list to Firestore before uploads
				const orderedImages = validImageNames.map(fileName => ({
					fileName: `${fileName}.webp`
				}));

				try {
					const projectRef = doc(db, 'projects', projectId);
					await updateDoc(projectRef, {
						images: orderedImages,
						imageCount: orderedImages.length
					});
					console.log(`✅ Written ${orderedImages.length} images to Firestore in CSV order`);
				} catch (error) {
					console.error('❌ Error writing images array to Firestore:', error);
				}

				setIsCSVSanitized(true);
			},
			error: (err) => {
				console.error('[sanitizeCSV] Parse error:', err.message || err);
			},
		});
	};

	useEffect(() => {
		if (isCSVSanitized) {
			uploadFiles(addedProjectId);
		}
	}, [isCSVSanitized]);

	useEffect(() => {
		const fetchSectionCount = async () => {
			const q = query(collection(db, 'projects'), where('directoryId', '==', directoryId));
			const snapshot = await getDocs(q);
			validateProjectTitle(`Section ${snapshot.size + 1}`);
		};
		fetchSectionCount();
	}, []);

	const addProject = async () => {
		const projectPayload = {
			name: projectName,
			creationDate: new Date(),
			images: [],
			organizationId,
			directoryId,
		};

		try {
			const projectsCollectionRef = collection(db, 'projects');
			const addedProjectRef = await addDoc(projectsCollectionRef, projectPayload);
			const projectId = addedProjectRef.id;
			setAddedProjectId(projectId);
			sanitizeCSV(projectId); // Pass projectId directly
		} catch (error) {
			console.error('Error adding project: ', error);
		}
	};

	const onBackClick = () => {
		navigate(`/admin/organizations/${organizationId}/directories/${directoryId}/projects`);
	};

	// Helper: resolve step state modifier
	const stepState = (index) => {
		if (index < activeStep) return 'completed';
		if (index === activeStep) return 'active';
		return 'upcoming';
	};

	return (
		<div className="np__root">
			<Header />
			<div className="np__content">
				{/* Back navigation */}
				<button
					className="np__back-btn"
					onClick={onBackClick}
					disabled={activeStep === 3 && !uploadComplete}
				>
					<ArrowBackIcon />
					Back to sections
				</button>

				{/* Page header */}
				<div className="np__page-header">
					<h1 className="np__page-title">Add a new section</h1>
					<p className="np__page-subtitle">
						Create a section and upload route data and captured imagery.
					</p>
				</div>

				{/* Two-column layout */}
				<div className="np__layout">
					{/* Left: custom vertical stepper */}
					<nav className="np__stepper" aria-label="Section setup steps">
						{steps.map((step, index) => {
							const state = stepState(index);
							return (
								<div key={step.label} className={`np__step np__step--${state}`}>
									<div className="np__step-indicator">
										{state === 'completed'
											? <CheckIcon className="np__step-check" />
											: <span>{index + 1}</span>
										}
									</div>
									<div className="np__step-text">
										<p className="np__step-label">{step.label}</p>
										<p className="np__step-sublabel">{step.sub}</p>
									</div>
								</div>
							);
						})}
					</nav>

					{/* Right: active step panel */}
					<div className="np__main">
						{/* Step 0 — Project details */}
						{activeStep === 0 && (
							<div className="np__panel">
								<div className="np__panel-header">
									<span className="np__panel-step-badge">Step 1 of 3</span>
									<h2 className="np__panel-title">Section details</h2>
									<p className="np__panel-desc">
										Give this section a clear name so your team can identify it at a glance.
									</p>
								</div>
								<div className="np__field-group">
									<label className="np__field-label" htmlFor="project-title-input">
										Section title
									</label>
									<input
										id="project-title-input"
										className={`np__field-input${projectNameInvalid ? ' np__field-input--error' : ''}`}
										placeholder="Enter section title"
										value={projectName}
										onChange={onProjectTitleChange}
										autoFocus
									/>
									{projectNameInvalid && (
										<p className="np__field-error">Please enter a section title.</p>
									)}
									{!projectNameInvalid && (
										<p className="np__field-helper">
											Use a clear name that helps your team identify this inspection section.
										</p>
									)}
								</div>
								<ActionButtons
									onBackClick={handleBack}
									onNextClick={handleNext}
									backButtonDisabled={true}
									nextButtonDisabled={false}
									step={0}
								/>
							</div>
						)}

						{/* Step 1 — Upload CSV */}
						{activeStep === 1 && (
							<div className="np__panel">
								<div className="np__panel-header">
									<span className="np__panel-step-badge">Step 2 of 3</span>
									<h2 className="np__panel-title">Upload CSV</h2>
									<p className="np__panel-desc">
										Add the CSV file that maps the inspection route data for this section.
									</p>
								</div>
								<UploadComponent
									step={1}
									activeStep={activeStep}
									csvFile={csvFile}
									setCSVFile={setCSVFile}
									images={images}
									setImages={setImages}
								/>
								<ActionButtons
									onBackClick={handleBack}
									onNextClick={handleNext}
									backButtonDisabled={false}
									nextButtonDisabled={!csvFile}
									step={1}
								/>
							</div>
						)}

						{/* Step 2 — Upload images */}
						{activeStep === 2 && (
							<div className="np__panel">
								<div className="np__panel-header">
									<span className="np__panel-step-badge">Step 3 of 3</span>
									<h2 className="np__panel-title">Upload images</h2>
									<p className="np__panel-desc">
										Add the captured route imagery associated with this inspection section.
									</p>
								</div>
								<UploadComponent
									step={2}
									activeStep={activeStep}
									csvFile={csvFile}
									setCSVFile={setCSVFile}
									images={images}
									setImages={setImages}
								/>
								<ActionButtons
									onBackClick={handleBack}
									onNextClick={handleNext}
									backButtonDisabled={false}
									nextButtonDisabled={images.length === 0}
									step={2}
								/>
							</div>
						)}

						{/* Step 3 — Processing & upload */}
						{activeStep === 3 && (
							<div className="np__progress-panel">
								<div className="np__progress-header">
									<h2 className="np__progress-title">Processing &amp; uploading</h2>
									<p className="np__progress-subtitle">
									Your section is being created. Please keep this page open until the upload completes.
									</p>
								</div>
								<div className="np__progress-items">
									<div>
										<p className="np__progress-item-label">CSV upload</p>
										<LinearProgressWithLabel value={csvUploadProgress} />
									</div>
									<div>
										<p className="np__progress-item-label">Image upload</p>
										<LinearProgressWithLabel value={Math.floor((imageUploadCount / totalImages) * 100) || 0} />
										<p className="np__progress-count">
											{imageUploadCount} of {totalImages} images uploaded
										</p>
									</div>
								</div>
							{uploadComplete && imageUploadFailed === 0 && (
								<div className="np__success-state">
								<p className="np__success-label">Section created successfully</p>
								<p className="np__success-sub">
									All files have been uploaded and the section is ready.
								</p>
								<button className="np__btn-back-to-projects" onClick={onBackClick}>
									Back to sections
									</button>
								</div>
							)}
							{uploadComplete && imageUploadFailed > 0 && (
								<div className="np__success-state">
								<p className="np__success-label">Section created with warnings</p>
								<p className="np__success-sub">
									{imageUploadCount} of {totalImages} images uploaded successfully. {imageUploadFailed} image{imageUploadFailed > 1 ? 's' : ''} failed — please retry or contact support.
									</p>
									<button className="np__btn-back-to-projects" onClick={onBackClick}>
										Back to sections
										</button>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddProject;
